import {
  folderAndDescendants, normalizeDefinition, type ReportDefinition, type ReportFolder, type ReportResult, type ReportType,
  reportResultFromSummaries,
} from '@olytic/foundation/reports';

import { REPORT_TIME_ZONE, REPORT_WEEK_STARTS_ON, runReportOnServer } from './reportSchema';
import { loadReport, type ReportLibrary, type SavedReport, withDefaultColumns } from './reportStore';

// A dashboard is a saved-report folder shown as tiles (MOTHER-151): no table and no
// access rule of its own. Its reports are the library's (GET /api/reports/library, which
// lists only what the viewer may see), and each tile runs through POST /api/reports/run,
// which applies record access as the report's own page does.

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** The most tiles a dashboard shows. */
export const DASHBOARD_TILE_LIMIT = 12;
/** The most tiles loading and running at once. */
export const DASHBOARD_RUNS_AT_ONCE = 3;

export interface DashboardReports {
  /** The reports shown, in the library's order: at most DASHBOARD_TILE_LIMIT. */
  reports: SavedReport[];
  /** How many reports the dashboard holds, shown or not. */
  total: number;
}

export interface DashboardContents extends DashboardReports {
  folder: ReportFolder;
}

/** The first DASHBOARD_TILE_LIMIT of `reports`, and how many there are. */
const capped = (reports: SavedReport[]): DashboardReports => ({ reports: reports.slice(0, DASHBOARD_TILE_LIMIT), total: reports.length });

/**
 * The saved reports in a folder and its sub-folders, in the library's order; null when the
 * viewer has no such folder (unknown, or not theirs to see).
 */
export function dashboardContents(library: ReportLibrary, folderId: string): DashboardContents | null {
  const folder = library.folders.find((f) => f.id === folderId);
  if (!folder) return null;
  const inside = folderAndDescendants(library.folders, folder.id);
  return { folder, ...capped(library.reports.filter((r) => inside.has(r.folderId))) };
}

/**
 * My Dashboard (MOTHER-293): the viewer's favorite reports, in the library's order. The
 * library marks `favorite` from the caller's own stars (ops.saved_report_favorite), so these
 * are never anyone else's. `removed` are reports the viewer has just taken off it (MOTHER-301):
 * they leave at once, before the library reloads, and the next favorite takes their place.
 */
export function favoriteContents(library: ReportLibrary, removed: ReadonlySet<string> = new Set()): DashboardReports {
  return capped(library.reports.filter((r) => r.favorite && !removed.has(r.id)));
}

/**
 * "Updated 3:42 PM" (MOTHER-298): the time a dashboard's last tile finished, in the report time
 * zone, as its header shows it.
 */
export function updatedText(at: number | Date, timeZone: string = REPORT_TIME_ZONE): string {
  return `Updated ${new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone })}`;
}

/** Runs jobs at most `limit` at a time, starting them in the order they were queued. */
export function createLimiter(limit: number): <T>(job: () => Promise<T>) => Promise<T> {
  let active = 0;
  const waiting: (() => void)[] = [];
  const next = () => {
    if (active >= limit) return;
    const start = waiting.shift();
    if (!start) return;
    active += 1;
    start();
  };
  return <T>(job: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      waiting.push(() => {
        Promise.resolve()
          .then(job)
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            next();
          });
      });
      next();
    });
}

export interface ReadyTile {
  state: 'ready';
  definition: ReportDefinition;
  reportType: ReportType;
  result: ReportResult;
}

/** Loads one saved report's definition (GET /api/reports/saved/:id) and runs it on the server. */
export async function runDashboardTile(fetcher: Fetch, id: string, types: ReportType[], signal: AbortSignal): Promise<ReadyTile> {
  signal.throwIfAborted();
  const report = await loadReport(fetcher, id);
  signal.throwIfAborted();
  const reportType = types.find((t) => t.key === report.reportType);
  if (!reportType || !report.definition) throw new Error('This report’s records aren’t available to you.');
  const definition = normalizeDefinition(withDefaultColumns(report.definition, reportType), reportType);
  const data = await runReportOnServer(fetcher, definition, signal);
  return {
    state: 'ready',
    definition,
    reportType,
    result: reportResultFromSummaries(definition, reportType, data, { timeZone: REPORT_TIME_ZONE, weekStartsOn: REPORT_WEEK_STARTS_ON }),
  };
}
